import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';

import FullPageLayout from 'components/Layout/FullPageLayout';
import Loading from 'components/Loading';
import RedirectAs404 from 'components/RedirectAs404';
import SignUpDropOffRedirect from 'components/SignUpPage/DropOffRedirect';
import roleChecks, { and } from 'utils/roles';
import lazyWithRetry from 'utils/lazyWithRetry';
import { ROSTER_UPLOAD_PATH } from 'lib/roster/constants';

import PublicRoute from './PublicRoute';
import ProtectedRoute from './ProtectedRoute';
import { Page } from './routes';

// We put exact because we want to redirect user to 404 in the case /login/someRandomPath
export const ValidRoutes = React.memo(() => {
  return (
    <SignUpDropOffRedirect>
      <React.Suspense fallback={<Loading />}>
        <Switch>
          <Redirect exact from="/" to="/videos" />
          <Route exact path="/sso-login/:email" component={lazyWithRetry(Page.Login)} />
          <PublicRoute exact path="/login" component={lazyWithRetry(Page.LoginMultiStep)} />
          <PublicRoute exact path="/login-cms" component={lazyWithRetry(Page.LoginUpgrade)} />
          <ProtectedRoute exact path="/verify-email" component={lazyWithRetry(Page.VerifyEmail)} />
          <PublicRoute exact path="/2fa" component={lazyWithRetry(Page.TwoFactorAuthentication)} />
          <PublicRoute
            exact
            path="/password-security-upgrade"
            component={lazyWithRetry(Page.PasswordSecurityUpgrade)}
          />
          <Route exact path="/oauth-redirect" component={lazyWithRetry(Page.OAuthDropOf)} />
          <Route
            exact
            path="/signup/create-account"
            component={lazyWithRetry(Page.SignupCreateAccount)}
          />
          <Route
            exact
            path="/signup/payment-checkout"
            component={lazyWithRetry(Page.SignUpPaymentCheckout)}
          />
          <Route
            exact
            path="/signup/payment-checkout-success"
            component={lazyWithRetry(Page.SignUpPaymentCheckout)}
          />
          <PublicRoute exact path="/invite" component={lazyWithRetry(Page.InviteSetPassword)} />
          <PublicRoute
            exact
            path="/forgot-password"
            component={lazyWithRetry(Page.SendResetEmail)}
          />

          <PublicRoute exact path="/new-password" component={lazyWithRetry(Page.ChangePassword)} />
          <PublicRoute exact path="/auth-redirect" component={lazyWithRetry(Page.AuthRedirect)} />
          <Route exact path="/error" component={lazyWithRetry(Page.InternalError)} />
          <ProtectedRoute
            exact
            path="/2fa-unenroll"
            component={lazyWithRetry(Page.TwoFactorAuthenticationUnenroll)}
          />
          <FullPageLayout>
            <Switch>
              <Route
                exact
                path="/invite/set-user-info"
                component={lazyWithRetry(Page.InviteSetAdminUserInfo)}
              />
              <Route exact path="/setup/manager" component={lazyWithRetry(Page.SetupManager)} />
              <Route
                exact
                path="/setup/organization"
                component={lazyWithRetry(Page.SetupOrganization)}
              />
              <Route exact path="/setup/waiting" component={lazyWithRetry(Page.SetupWaiting)} />
              <ProtectedRoute
                exact
                path="/invalid-subscription"
                component={lazyWithRetry(Page.InvalidSubscription)}
              />
              <ProtectedRoute exact path="/videos" component={lazyWithRetry(Page.Videos)} />
              <ProtectedRoute
                exact
                path="/uploaded-videos"
                component={lazyWithRetry(Page.Videos)}
              />
              <ProtectedRoute
                exact
                path="/storymakers"
                component={lazyWithRetry(Page.StoryMakers)}
              />
              <ProtectedRoute
                exact
                path={ROSTER_UPLOAD_PATH}
                component={lazyWithRetry(Page.RosterUpload)}
              />

              <ProtectedRoute exact path="/reviewers" component={lazyWithRetry(Page.Reviewers)} />
              <ProtectedRoute
                exact
                path="/filming-groups"
                component={lazyWithRetry(Page.FilmingGroups)}
              />
              <ProtectedRoute
                exact
                path="/template/:id"
                component={lazyWithRetry(Page.TemplateDetail)}
              />
              <ProtectedRoute
                exact
                path="/template/:id/speaking-script"
                roleChecks={roleChecks.isNotReviewer}
                component={lazyWithRetry(Page.TemplateDetailSpeakingScript)}
              />
              <ProtectedRoute
                exact
                path="/settings/brand"
                component={lazyWithRetry(Page.SettingsBrand)}
              />
              <ProtectedRoute
                exact
                path="/settings/subscription"
                roleChecks={and(roleChecks.isPrimary, roleChecks.isManager)}
                component={lazyWithRetry(Page.SettingsSubscription)}
              />
              <ProtectedRoute
                exact
                path="/organizations"
                component={lazyWithRetry(Page.Organizations)}
                roleChecks={roleChecks.isAdministrator}
              />
              {/* Must precede /organizations/:id, which would otherwise match it. */}
              <ProtectedRoute
                exact
                path="/organizations/recycle-bin"
                component={lazyWithRetry(Page.Organizations)}
                roleChecks={roleChecks.canManageOrganizationLifecycle}
              />
              <ProtectedRoute
                exact
                path="/organizations/:id"
                component={lazyWithRetry(Page.OrganizationDetailDetails)}
                roleChecks={roleChecks.isAdministrator}
              />
              <ProtectedRoute
                exact
                path="/organizations/:id/filming-groups"
                component={lazyWithRetry(Page.OrganizationDetailFilmingGroups)}
                roleChecks={roleChecks.isAdministrator}
              />
              <ProtectedRoute
                exact
                path="/organizations/:id/filming-groups/create"
                component={lazyWithRetry(Page.OrganizationDetailFilmingGroupCreate)}
                roleChecks={roleChecks.isAdministrator}
              />
              <ProtectedRoute
                exact
                path="/organizations/:id/roster-mappings"
                component={lazyWithRetry(Page.OrganizationDetailRosterMappings)}
                roleChecks={roleChecks.isAdministrator}
              />
              <ProtectedRoute
                exact
                path="/organizations/:id/roster-mappings/:mappingId/edit"
                component={lazyWithRetry(Page.OrganizationDetailRosterMappingEdit)}
                roleChecks={roleChecks.isAdministrator}
              />
              <ProtectedRoute
                exact
                path="/(admins|managers)"
                component={lazyWithRetry(Page.AdminUsers)}
                roleChecks={roleChecks.isAdministrator}
              />
              <ProtectedRoute
                path="/settings/organization"
                component={lazyWithRetry(Page.SettingsOrganization)}
              />
              <ProtectedRoute
                exact
                path="/settings/managers"
                component={lazyWithRetry(Page.SettingsAdminUsers)}
              />
              <ProtectedRoute
                exact
                path="/(admins|managers)/:id"
                roleChecks={roleChecks.isAdministrator}
                component={lazyWithRetry(Page.AdminUserDetail)}
              />
              <ProtectedRoute
                exact
                path="/storymakers/:id"
                component={lazyWithRetry(Page.StorymakerDetail)}
              />
              <ProtectedRoute
                exact
                path="/templates/:id"
                roleChecks={roleChecks.isAdministrator}
                component={lazyWithRetry(Page.TemplateDetailAdminDetails)}
              />
              <ProtectedRoute
                exact
                path="/templates/:id/filming-guide"
                roleChecks={roleChecks.isAdministrator}
                component={lazyWithRetry(Page.TemplateDetailAdminFilmingGuide)}
              />
              <ProtectedRoute
                exact
                path="/templates/:id/speaking-script"
                roleChecks={roleChecks.isAdministrator}
                component={lazyWithRetry(Page.TemplateDetailAdminSpeakingScript)}
              />
              <ProtectedRoute
                exact
                path="/templates"
                roleChecks={roleChecks.isAdministrator}
                component={lazyWithRetry(Page.Templates)}
              />

              <ProtectedRoute
                exact
                path="/expired-password"
                component={lazyWithRetry(Page.ExpiredPassword)}
              />
              <ProtectedRoute
                exact
                path="/videos/create"
                component={lazyWithRetry(Page.CreateVideo)}
                roleChecks={roleChecks.isAdministratorOrNonNowManager}
              />
              <ProtectedRoute
                exact
                path="/videos/:id"
                component={lazyWithRetry(Page.MediaDetail)}
              />
              <ProtectedRoute
                exact
                path="/uploaded-videos/:id"
                component={lazyWithRetry(Page.MediaDetail)}
              />
              <ProtectedRoute path="/training/:name" component={lazyWithRetry(Page.Training)} />
              <RedirectAs404 />
            </Switch>
          </FullPageLayout>
          <RedirectAs404 />
        </Switch>
      </React.Suspense>
    </SignUpDropOffRedirect>
  );
});
